import React, { FC, Fragment, useCallback } from 'react';
import {
  Banner,
  Button, type ButtonProps,
  useBreakpoint,
} from '@printdeal/ui-library';
import { Image, Source } from '@unpic/react';
import { useInView } from 'react-intersection-observer';

import {
  handleOnBannerClick,
  observerCallbackBannerView,
  handleMultiVariantBannerClick,
} from './PrimaryBanner.events';
import { HomepageBannerProps } from '../types';
import { getJustifyValue } from './helper';

export const PrimaryBanner: FC<HomepageBannerProps> = ({
  className,
  bannerData,
  orientation = 'horizontal',
  audiencesDataFromSegment,
  audienceOverRideForPlayground,
}) => {
  const callBackForInViewObserver = useCallback((inView: boolean, entry: IntersectionObserverEntry) => {
    observerCallbackBannerView(
      inView,
      entry,
      bannerData,
      audiencesDataFromSegment,
      audienceOverRideForPlayground,
    );
  }, [audiencesDataFromSegment, audienceOverRideForPlayground]);

  const { ref } = useInView({
    threshold: 0.7,
    onChange: callBackForInViewObserver,
    triggerOnce: true,
  });

  const isMobile = useBreakpoint('max-lg');

  if (!bannerData) return null;
  const {
    alignment = 'right',
    displayTitle = '',
    buttonKind = 'secondary',
    buttonUrl,
    buttonText,
    mediaDesktop: imageVertical,
    mediaMobile: imageHorizontal,
    text,
    audienceType,
  } = bannerData!;
  const { url: imgUrl, height, width } = orientation === 'horizontal' ? imageHorizontal : imageVertical;

  if (!imgUrl) {
    return null;
  }

  const variant = isMobile ? 'vertical' : orientation;
  return (
    <div className={className}>
      <Banner
        button={buttonText
          ? (<Button as="span" variant={buttonKind.toLowerCase() as ButtonProps['variant']}>{buttonText}</Button>)
          : undefined}
        className={variant === 'vertical' ? 'pd-min-h-[36rem] lg:pd-h-full lg:pd-min-h-0 xl:pd-h-full' : 'lg:pd-min-h-[22.5rem]'}
        data-banner-title={displayTitle}
        data-audience-type={audienceType}
        data-testid="homepage-banner"
        justify={getJustifyValue(alignment)}
        link={buttonUrl ? <a href={buttonUrl} title={displayTitle}>{displayTitle}</a> : undefined}
        onClick={() => {
          handleMultiVariantBannerClick(
            audienceType,
          );
          handleOnBannerClick(displayTitle);
        }}
        ref={ref}
        title={displayTitle || ''}
        variant={variant}
        image={(
          <picture className="pd-cover-parent pd-object-top">
            {['avif', 'webp', null].map((format) => (
              <Fragment key={format || 'default'}>
                <Source
                  src={`${imgUrl}?fm=${format || ''}`}
                  media={(variant === 'horizontal' && '(max-width: 991.5px)') || ''}
                  width={width}
                  height={height}
                  sizes="100vw"
                  type={format && `image/${format}`}
                  breakpoints={[320, 640, 950]}
                />
              </Fragment>
            ))}
            <Image
              src={imgUrl}
              layout="fullWidth"
              alt=""
              priority
              unstyled
              className="pd-cover-parent"
            />
          </picture>
        )}
      >
        {text?.text && (
          <p>
            {text.text}
          </p>
        )}
      </Banner>
    </div>
  );
};
